import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { clearAuthToken, getAuthToken, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);

  const fetchUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }

    try {
      const { data } = await api.get("/user");
      setUser(data.user);
      return data.user;
    } catch {
      clearAuthToken();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/login", { email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, login_token: data.login_token, user: data.user };
    }
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const verifyTwoFactor = useCallback(async (loginToken, code) => {
    const { data } = await api.post("/login/2fa", { login_token: loginToken, code });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const updateContext = useCallback(async (branchId, counterId = null) => {
    setContextLoading(true);
    try {
      const { data } = await api.patch("/user/context", {
        branch_id: branchId,
        counter_id: counterId,
      });
      setUser(data.user);
      return data.user;
    } finally {
      setContextLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/logout");
    } catch {
      // ignore — token may already be invalid
    } finally {
      clearAuthToken();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      contextLoading,
      login,
      verifyTwoFactor,
      logout,
      updateContext,
      refreshUser: fetchUser,
    }),
    [user, loading, contextLoading, login, verifyTwoFactor, logout, updateContext, fetchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
